import * as THREE from 'three';
import { TerrainFeature } from './TerrainFeature';
import { MountainRidgeConfig, TerrainBounds, TERRAIN_COLORS } from './TerrainConfig';

/**
 * Mountain Ridge - Linear ridge terrain connecting peaks.
 * Height range: 15-25m for Tier 3 highway access.
 * Creates natural pathways across the terrain.
 */
export class MountainRidge extends TerrainFeature {
  private startPoint: THREE.Vector3;
  private endPoint: THREE.Vector3;
  private peakHeight: number;
  private width: number;
  private ridgeLength: number;
  private ridgeDirection: THREE.Vector3;
  private ridgeNormal: THREE.Vector3;

  constructor(scene: THREE.Scene, config: MountainRidgeConfig) {
    // Calculate center position between start and end
    const center = config.startPoint.clone().add(config.endPoint).multiplyScalar(0.5);

    super(scene, center, 0, 'mountain_ridge');

    this.startPoint = config.startPoint.clone();
    this.endPoint = config.endPoint.clone();
    this.peakHeight = config.peakHeight;
    this.width = config.width;

    // Calculate ridge properties
    this.ridgeDirection = this.endPoint.clone().sub(this.startPoint).normalize();
    this.ridgeLength = this.startPoint.distanceTo(this.endPoint);

    // Normal perpendicular to ridge direction (horizontal plane)
    this.ridgeNormal = new THREE.Vector3(-this.ridgeDirection.z, 0, this.ridgeDirection.x);

    this.createMesh();
    this.addToScene();
  }

  private createMesh(): void {
    // Create ridge geometry as an extruded profile
    const lengthSegments = Math.ceil(this.ridgeLength / 5);
    const widthSegments = 12;
    const geometry = new THREE.BufferGeometry();
    const vertices: number[] = [];
    const indices: number[] = [];

    // Generate vertices along the ridge
    for (let l = 0; l <= lengthSegments; l++) {
      const lengthProgress = l / lengthSegments;

      // Position along ridge line
      const ridgePoint = this.startPoint.clone().lerp(this.endPoint, lengthProgress);

      // Height variation along ridge (slightly lower at ends)
      const endTaper = 1 - 0.3 * Math.pow(2 * lengthProgress - 1, 2);
      const ridgeHeight = this.peakHeight * endTaper;

      for (let w = 0; w <= widthSegments; w++) {
        const widthProgress = w / widthSegments;
        const widthOffset = (widthProgress - 0.5) * this.width;

        // Position across ridge width
        const x = ridgePoint.x + this.ridgeNormal.x * widthOffset;
        const z = ridgePoint.z + this.ridgeNormal.z * widthOffset;

        // Height profile across width (peak in center)
        const widthFactor = Math.cos(widthOffset / (this.width / 2) * Math.PI / 2);
        const height = ridgeHeight * Math.max(0, widthFactor);

        // Convert to local coordinates (relative to center)
        vertices.push(x - this.position.x, height, z - this.position.z);
      }
    }

    // Generate indices
    for (let l = 0; l < lengthSegments; l++) {
      for (let w = 0; w < widthSegments; w++) {
        const a = l * (widthSegments + 1) + w;
        const b = a + 1;
        const c = a + (widthSegments + 1);
        const d = c + 1;

        indices.push(a, c, b);
        indices.push(b, c, d);
      }
    }

    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(vertices, 3)
    );
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    // Main ridge mesh
    const material = this.createTerrainMaterial(TERRAIN_COLORS.BASE);
    const ridgeMesh = new THREE.Mesh(geometry, material);
    this.mesh.add(ridgeMesh);

    // Add ridge crest highlight
    this.createCrestHighlight();

    // Add grid lines
    this.createGridLines();
  }

  private createCrestHighlight(): void {
    // Glowing line along the ridge crest
    const points: THREE.Vector3[] = [];
    const segments = 20;

    for (let i = 0; i <= segments; i++) {
      const progress = i / segments;
      const endTaper = 1 - 0.3 * Math.pow(2 * progress - 1, 2);
      const ridgePoint = this.startPoint.clone().lerp(this.endPoint, progress);

      points.push(
        new THREE.Vector3(
          ridgePoint.x - this.position.x,
          this.peakHeight * endTaper + 0.2,
          ridgePoint.z - this.position.z
        )
      );
    }

    const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
    const lineMaterial = new THREE.LineBasicMaterial({
      color: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      transparent: true,
      opacity: 0.8,
    });

    const crestLine = new THREE.Line(lineGeometry, lineMaterial);
    this.mesh.add(crestLine);

    // Add glow tube along crest
    const tubeGeometry = new THREE.TubeGeometry(
      new THREE.CatmullRomCurve3(points),
      segments,
      0.3,
      8,
      false
    );
    const tubeMaterial = new THREE.MeshStandardMaterial({
      color: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      emissive: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      emissiveIntensity: 1.5,
      transparent: true,
      opacity: 0.6,
    });

    const crestTube = new THREE.Mesh(tubeGeometry, tubeMaterial);
    this.mesh.add(crestTube);
  }

  private createGridLines(): void {
    const lineMaterial = new THREE.LineBasicMaterial({
      color: TERRAIN_COLORS.GRID_LINE,
      transparent: true,
      opacity: 0.3,
    });

    // Cross-ridge lines
    const numLines = Math.ceil(this.ridgeLength / 15);
    for (let l = 1; l < numLines; l++) {
      const progress = l / numLines;
      const endTaper = 1 - 0.3 * Math.pow(2 * progress - 1, 2);
      const ridgePoint = this.startPoint.clone().lerp(this.endPoint, progress);
      const ridgeHeight = this.peakHeight * endTaper;

      const points: THREE.Vector3[] = [];

      for (let w = 0; w <= 20; w++) {
        const widthProgress = w / 20;
        const widthOffset = (widthProgress - 0.5) * this.width;
        const widthFactor = Math.cos(widthOffset / (this.width / 2) * Math.PI / 2);
        const height = ridgeHeight * Math.max(0, widthFactor);

        points.push(
          new THREE.Vector3(
            ridgePoint.x + this.ridgeNormal.x * widthOffset - this.position.x,
            height + 0.1,
            ridgePoint.z + this.ridgeNormal.z * widthOffset - this.position.z
          )
        );
      }

      const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(lineGeometry, lineMaterial);
      this.mesh.add(line);
    }
  }

  /**
   * Get height at world position using ridge profile.
   */
  getHeightAtPosition(worldPos: THREE.Vector3): number | null {
    // Project position onto ridge line
    const toPos = new THREE.Vector3(
      worldPos.x - this.startPoint.x,
      0,
      worldPos.z - this.startPoint.z
    );

    // Progress along ridge (0 to 1)
    const lengthProgress = toPos.dot(this.ridgeDirection) / this.ridgeLength;

    // Check if within ridge length
    if (lengthProgress < -0.1 || lengthProgress > 1.1) {
      return null;
    }

    // Distance from ridge center line
    const distanceFromRidge = Math.abs(toPos.dot(this.ridgeNormal));

    // Check if within ridge width
    if (distanceFromRidge > this.width / 2 + 5) {
      return null;
    }

    // Height along ridge (tapered at ends)
    const clampedProgress = Math.max(0, Math.min(1, lengthProgress));
    const endTaper = 1 - 0.3 * Math.pow(2 * clampedProgress - 1, 2);
    const ridgeHeight = this.peakHeight * endTaper;

    // Height across width (peak at center)
    const widthRatio = distanceFromRidge / (this.width / 2);
    if (widthRatio > 1) {
      // Smooth falloff beyond edge
      const falloff = Math.exp(-(widthRatio - 1) * 3);
      return ridgeHeight * falloff * 0.5;
    }

    const widthFactor = Math.cos(widthRatio * Math.PI / 2);
    const height = ridgeHeight * Math.max(0, widthFactor);

    if (height < 0.5) {
      return null;
    }

    return height;
  }

  getBounds(): TerrainBounds {
    const padding = this.width / 2 + 10;
    return {
      minX: Math.min(this.startPoint.x, this.endPoint.x) - padding,
      maxX: Math.max(this.startPoint.x, this.endPoint.x) + padding,
      minZ: Math.min(this.startPoint.z, this.endPoint.z) - padding,
      maxZ: Math.max(this.startPoint.z, this.endPoint.z) + padding,
    };
  }
}
